import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { useConsent } from "@/components/CookieConsent";

const SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_URL =
  "https://calendly.com/sarazoldan/vibecheck?hide_event_type_details=1&hide_gdpr_banner=1";

// One shared loader prevents duplicate script tags during route changes and hot reload.
function loadCalendly() {
  if (window.Calendly) return Promise.resolve(window.Calendly);
  if (window.__calendlyWidgetPromise) return window.__calendlyWidgetPromise;

  window.__calendlyWidgetPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Calendly), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.Calendly);
    script.onerror = () => reject(new Error("Calendly widget failed to load"));
    document.head.appendChild(script);
  });

  return window.__calendlyWidgetPromise;
}

export const BookingSection = () => {
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const consent = useConsent();

  useEffect(() => {
    if (consent !== "accepted") return;
    let cancelled = false;
    const container = containerRef.current;

    loadCalendly()
      .then((Calendly) => {
        if (cancelled || !container || !Calendly) return;
        container.replaceChildren();
        Calendly.initInlineWidget({
          url: CALENDLY_URL,
          parentElement: container,
          resize: true,
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (container) container.replaceChildren();
    };
  }, [consent]);

  return (
    <section
      id="enquiry"
      className="py-32 md:py-44"
      data-testid="booking-section"
    >
      <div className="max-w-3xl mx-auto px-6">
        <Reveal>
          <p className="text-xs tracking-[0.35em] uppercase text-[#7D1B56] mb-6 text-center">
            A First Conversation
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif-display font-light text-4xl sm:text-5xl leading-tight text-[#02515A] text-center">
            Choose a time <span className="italic text-[#7D1B56]">that feels right.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 text-base font-light leading-loose text-[#3F6B72] text-center max-w-xl mx-auto">
            A short, unhurried call — held in complete confidence. Pick a time
            below and it goes straight to Sara's calendar.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-14 border border-[#B4A893]/50 bg-[#FCF5EB] p-2 md:p-4">
            {consent === "accepted" && !failed ? (
              <div
                ref={containerRef}
                className="calendly-container w-full"
                style={{ minWidth: "320px", minHeight: "720px" }}
                data-testid="calendly-widget-container"
              />
            ) : (
              <div className="py-20 text-center px-6" data-testid="calendly-fallback">
                <p className="text-sm font-light text-[#3F6B72] leading-loose">
                  {consent === "accepted"
                    ? "The calendar could not load here."
                    : "The calendar appears once cookies are accepted below."}{" "}
                  You can always
                  <a
                    href="https://calendly.com/sarazoldan/vibecheck"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline underline-offset-4 ml-1 text-[#02515A]"
                    data-testid="calendly-fallback-link"
                  >
                    choose a time directly
                  </a>
                  .
                </p>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
